/// <reference types="youtube" />
import React, { useEffect, useRef } from "react";

interface YouTubePlayerProps {
  videoId: string;
  // Player dimensions
  width?: number | string;
  height?: number | string;
  // playerVars
  autoplay?: boolean;
  controls?: 0 | 1 | 2;
  loop?: boolean;
  start?: number;
  end?: number;
  rel?: boolean;
  fs?: boolean;
  modestbranding?: boolean;
  playsinline?: boolean;
  disablekb?: boolean;
  enablejsapi?: boolean;
  iv_load_policy?: 1 | 3;
  cc_load_policy?: 0 | 1;
  cc_lang_pref?: string;
  hl?: string;
  color?: "red" | "white";
  playlist?: string;
  listType?: "playlist" | "search" | "user_uploads";
  list?: string;
  // Events
  onReady?: (event: YT.PlayerEvent) => void;
  onStateChange?: (event: YT.OnStateChangeEvent) => void;
  onPlaybackQualityChange?: (event: YT.OnPlaybackQualityChangeEvent) => void;
  onPlaybackRateChange?: (event: YT.OnPlaybackRateChangeEvent) => void;
  onError?: (event: YT.OnErrorEvent) => void;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  width,
  height,
  autoplay = true,
  controls,
  loop,
  start,
  end,
  rel = false,
  fs,
  modestbranding = true,
  playsinline,
  disablekb,
  enablejsapi,
  iv_load_policy,
  cc_load_policy,
  cc_lang_pref,
  hl,
  color,
  playlist,
  listType,
  list,
  onReady,
  onStateChange,
  onPlaybackQualityChange,
  onPlaybackRateChange,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);

  useEffect(() => {
    const initPlayer = () => {
      if (!containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        ...(width !== undefined && { width }),
        ...(height !== undefined && { height }),
        videoId,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          rel: rel ? 1 : 0,
          modestbranding: modestbranding ? 1 : 0,
          ...(controls !== undefined && { controls }),
          ...(loop !== undefined && { loop: loop ? 1 : 0 }),
          ...(start !== undefined && { start }),
          ...(end !== undefined && { end }),
          ...(fs !== undefined && { fs: fs ? 1 : 0 }),
          ...(playsinline !== undefined && {
            playsinline: playsinline ? 1 : 0,
          }),
          ...(disablekb !== undefined && { disablekb: disablekb ? 1 : 0 }),
          ...(enablejsapi !== undefined && {
            enablejsapi: enablejsapi ? 1 : 0,
          }),
          ...(iv_load_policy !== undefined && { iv_load_policy }),
          ...(cc_load_policy !== undefined && { cc_load_policy }),
          ...(cc_lang_pref !== undefined && { cc_lang_pref }),
          ...(hl !== undefined && { hl }),
          ...(color !== undefined && { color }),
          ...(playlist !== undefined && { playlist }),
          ...(listType !== undefined && { listType }),
          ...(list !== undefined && { list }),
        },
        events: {
          ...(onReady && { onReady }),
          ...(onStateChange && { onStateChange }),
          ...(onPlaybackQualityChange && { onPlaybackQualityChange }),
          ...(onPlaybackRateChange && { onPlaybackRateChange }),
          ...(onError && { onError }),
        },
      });
    };

    if (window.YT?.Player) {
      initPlayer();
    } else {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(script);
      (
        window as unknown as { onYouTubeIframeAPIReady: () => void }
      ).onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      playerRef.current?.destroy();
    };
  }, [
    videoId,
    autoplay,
    width,
    height,
    controls,
    loop,
    start,
    end,
    rel,
    fs,
    modestbranding,
    playsinline,
    disablekb,
    enablejsapi,
    iv_load_policy,
    cc_load_policy,
    cc_lang_pref,
    hl,
    color,
    playlist,
    listType,
    list,
    onReady,
    onStateChange,
    onPlaybackQualityChange,
    onPlaybackRateChange,
    onError,
  ]);

  return <div ref={containerRef} />;
};

export default YouTubePlayer;
